import Partner from './Partner';
import Practitioner from './Practitioner';
import { KitSurveyStatus } from './Survey';
import TinyAccount, { TinyAccountId } from './TinyAccount';

export enum KitType {
  STOOL = 'stool',
  VAGINAL = 'vaginal',
}

export const KitStatusValues = {
  ACTIVATED: 'activated',
  CONFIRM_KIT_RETURNED_TO_LAB_BY_CUSTOMER:
    'confirm_kit_returned_to_lab_by_customer',
  DELIVERED_TO_CUSTOMER: 'delivered_to_customer',
  DELIVERED_TO_LAB: 'delivered_to_lab',
  EXPERT_REVIEW_COMPLETE: 'expert_review_complete',
  INITIALIZED: 'initialized',
  IN_TRANSIT_TO_CUSTOMER: 'in_transit_to_customer',
  IN_TRANSIT_TO_LAB: 'in_transit_to_lab',
  LABEL_EXPIRY_EMAIL_SENT: 'label_expiry_email_sent',
  PIPELINE_COMPLETE: 'pipeline_complete',
  SEQUENCES_DELIVERED: 'sequences_delivered',
  RESULTS_READY: 'results_ready',
  RESULTS_READY_PRACTITIONER: 'results_ready_practitioner',
  RETURN_MISSING: 'return_missing',
  VOID: 'void',
  VOID_CUSTOMER_REFUND: 'void_customer_refund',
  VOID_INTERNAL: 'void_internal',
  VOID_LAB_ERROR: 'void_lab_error',
  VOID_LOST_IN_MAIL: 'void_lost_in_mail',
  VOID_RETURN_LABEL_EXPIRED: 'void_return_label_expired',
  VOID_SAMPLE_EXPIRED: 'void_sample_expired',
  VOID_SHIPPO_SHIPMENT_FAILED: 'void_shippo_shipment_failed',
} as const;

export type KitStatus = (typeof KitStatusValues)[keyof typeof KitStatusValues];
export const KitStatuses = KitStatusValues;

export type KitId = string;

export interface KitShippingAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/**
 * Shipping details for a kit, resolved by the backend with the same precedence
 * used to send the fulfillment to the 3PL / generate the label.
 */
export interface KitShippingInfo {
  recipient_name: string;
  address: KitShippingAddress | null;
  tracking_number: string | null;
}

export interface Kit {
  adult_gut_sample_survey?: string | null;
  adult_vaginal_sample_survey?: string | null;
  age_at_sampling?: number | null;
  baby_gut_sample_survey?: string | null;
  date_actions_step_clicked?: string | null;
  date_activated?: string | null;
  date_delivered_to_customer?: string | null;
  date_delivered_to_lab?: string | null;
  date_expert_review_complete?: string | null;
  date_in_transit_to_customer?: string | null;
  date_in_transit_to_lab?: string | null;
  date_pipeline_complete?: string | null;
  date_purchased?: string | null;
  date_results_ready_practitioner?: string | null;
  date_results_ready?: string | null;
  date_results_step_clicked?: string | null;
  date_sequences_delivered?: string | null;
  date_survey_completed?: string | null;
  date_voided?: string | null;
  days_pp?: number | null;
  external_kit_id?: string | null;
  feeding_method?: string | null;
  id: KitId;
  is_resample?: boolean | number | null;
  lab_partner: string;
  partner_id?: string | null;
  partner?: Partner;
  pdf_url?: string | null;
  pending_components?: string[];
  post_partum?: boolean | number | null;
  practitioner?: Practitioner;
  practitioner_id?: string | null;
  pregnant?: boolean | number | null;
  results_state?: KitResultsState;
  sample_date?: string;
  sample_label?: string | null;
  sampling_type?: KitType;
  sci_initial_version?: string | null;
  sci_version?: string | null;
  status?: KitStatus;
  survey_status?: KitSurveyStatus;
  surveys_complete?: number;
  tinyaccount_id?: TinyAccountId;
  tracking_number_to_user?: string | number | null;
  tracking_number_to_lab?: string | number | null;
  ttc?: boolean | number | null;
  variants?: KitVariant[] | null;
  week_of_pregnancy?: number | null;
  tinyaccount?: Partial<TinyAccount> | null;
}

export type KitResultsState = 'pending' | 'partial' | 'complete';

export type KitVariant = {
  kit_id: string;
  lab_partner: string;
  /* OPTIONAL */
  id?: number;
  date_delivered_to_lab?: string | null;
  date_results_ready?: string | null;
  sample_date?: string | null;
  sampling_type?: string;
  tracking_number_to_lab?: string;
};

export default Kit;
